/**
 * Storage Client — Drop-in replacement for db.storage.
 *
 * Exposes the same chainable API shape that the codebase uses:
 *   storageClient.from(bucket).upload(path, file, { upsert, contentType })
 *   storageClient.from(bucket).remove([paths])
 *   storageClient.from(bucket).getPublicUrl(path)
 *   storageClient.from(bucket).createSignedUrl(path, expiresInSeconds)
 *   storageClient.from(bucket).download(path)
 *
 * All requests go through the backend's /storage/:bucket/* routes, which
 * proxy to S3 using task role credentials.
 */

import { getAccessToken } from "./api-client";

const API_BASE = import.meta.env.VITE_API_URL || "https://api.openhubr.com";

export interface UploadOptions {
  upsert?: boolean;
  contentType?: string;
  cacheControl?: string;
}

export interface UploadResult {
  data: { path: string; fullPath: string; id: string } | null;
  error: { message: string } | null;
}

export interface GetPublicUrlResult {
  data: { publicUrl: string };
}

export interface CreateSignedUrlResult {
  data: { signedUrl: string } | null;
  error: { message: string } | null;
}

export interface DownloadResult {
  data: Blob | null;
  error: { message: string } | null;
}

export interface RemoveResult {
  data: { path: string }[] | null;
  error: { message: string } | null;
}

function authHeader(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Translate raw storage errors (Fastify `FST_ERR_*`, S3 codes, plain HTTP)
 * into something a recruiter-facing UI can show. Falls through to the
 * original message so we don't accidentally hide novel failures.
 */
function friendlyStorageError(raw: unknown, status: number): string {
  const msg = typeof raw === "string" ? raw : "Erro desconhecido ao enviar arquivo.";
  const lower = msg.toLowerCase();
  if (lower.includes("body too large") || lower.includes("payload") || status === 413 || lower.includes("fst_err_ctp_body_too_large")) {
    return "Arquivo excedeu o tamanho máximo permitido. Reduza o arquivo e tente novamente.";
  }
  if (lower.includes("invalid bucket")) {
    return "Configuração de armazenamento inválida. Contate o suporte.";
  }
  if (status === 401 || lower.includes("authentication")) {
    return "Sua sessão expirou. Faça login novamente para continuar.";
  }
  if (status === 403) {
    return "Você não tem permissão para enviar arquivos neste destino.";
  }
  if (lower.includes("apenas arquivos pdf")) {
    return msg; // already pt-BR from server
  }
  if (lower.includes("network") || lower.includes("failed to fetch")) {
    return "Falha de conexão. Verifique sua internet e tente novamente.";
  }
  return msg;
}

function encodeKey(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

class BucketClient {
  constructor(private readonly bucket: string) {}

  async upload(
    path: string,
    file: File | Blob | ArrayBuffer | Uint8Array,
    options: UploadOptions = {}
  ): Promise<UploadResult> {
    try {
      let body: BodyInit;
      let contentType = options.contentType;

      if (file instanceof File) {
        body = file;
        contentType = contentType || file.type || "application/octet-stream";
      } else if (file instanceof Blob) {
        body = file;
        contentType = contentType || file.type || "application/octet-stream";
      } else if (file instanceof ArrayBuffer) {
        body = file;
        contentType = contentType || "application/octet-stream";
      } else {
        body = file as Uint8Array;
        contentType = contentType || "application/octet-stream";
      }

      const url = `${API_BASE}/storage/${this.bucket}/${encodeKey(path)}`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": contentType,
          ...authHeader(),
        },
        body,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        return { data: null, error: { message: friendlyStorageError(err.error || res.statusText, res.status) } };
      }

      const json = await res.json();
      return {
        data: { path: json.path, fullPath: `${this.bucket}/${json.path}`, id: json.path },
        error: null,
      };
    } catch (err: any) {
      return { data: null, error: { message: friendlyStorageError(err.message, 0) } };
    }
  }

  getPublicUrl(path: string): GetPublicUrlResult {
    // Matches the public URL served by S3 directly. We don't need to call the
    // backend for this — S3 objects in PUBLIC_BUCKETS are world-readable.
    const bucketPrefix = import.meta.env.VITE_STORAGE_BUCKET_PREFIX || "openhub-production";
    const region = import.meta.env.VITE_AWS_REGION || "us-east-1";
    const publicUrl = `https://${bucketPrefix}-${this.bucket}.s3.${region}.amazonaws.com/${encodeKey(path)}`;
    return { data: { publicUrl } };
  }

  async createSignedUrl(path: string, expiresIn = 3600): Promise<CreateSignedUrlResult> {
    try {
      const url = `${API_BASE}/storage/${this.bucket}/${encodeKey(path)}?signed=1&expires=${expiresIn}`;
      const res = await fetch(url, { headers: authHeader() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        return { data: null, error: { message: err.error || res.statusText } };
      }
      const json = await res.json();
      return { data: { signedUrl: json.signedUrl }, error: null };
    } catch (err: any) {
      return { data: null, error: { message: err.message || "Sign URL failed" } };
    }
  }

  async download(path: string): Promise<DownloadResult> {
    try {
      const url = `${API_BASE}/storage/${this.bucket}/${encodeKey(path)}?download=1`;
      const res = await fetch(url, { headers: authHeader() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        return { data: null, error: { message: err.error || res.statusText } };
      }
      const blob = await res.blob();
      return { data: blob, error: null };
    } catch (err: any) {
      return { data: null, error: { message: err.message || "Download failed" } };
    }
  }

  async remove(paths: string[]): Promise<RemoveResult> {
    try {
      const results: { path: string }[] = [];
      for (const path of paths) {
        const url = `${API_BASE}/storage/${this.bucket}/${encodeKey(path)}`;
        const res = await fetch(url, { method: "DELETE", headers: authHeader() });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          return { data: null, error: { message: err.error || res.statusText } };
        }
        results.push({ path });
      }
      return { data: results, error: null };
    } catch (err: any) {
      return { data: null, error: { message: err.message || "Remove failed" } };
    }
  }
}

class StorageClient {
  from(bucket: string): BucketClient {
    return new BucketClient(bucket);
  }
}

export const storageClient = new StorageClient();
